
const STORE_VERSION = 1;
const STORAGE_KEY = "los-toldos-crm:state";

// =========================
// 1) Estado inicial (mínimo y seguro)
// =========================
const initialState = {
  meta: {
    version: STORE_VERSION,
    bootedAt: new Date().toISOString(),
  },

  // Auth / usuario (cuando conectemos auth de verdad)
  auth: {
    user: null,
    session: null,
    status: "unknown", // "unknown" | "signed_in" | "signed_out"
  },

  // Equipo / perfil (cuando conectemos teamService)
  team: {
    teamId: null,
    profile: null,
    memberId: null,
    hasGigs: false,
  },

  // Entidades principales del CRM
  data: {
    events: [], // lista plana de eventos
    contacts: [], // lista plana de contactos
    venues: [], // lista plana de venues

    // tablas relación / satélites
    eventContacts: [], // event_contacts (si la usas)
    eventVenues: [], // event_venues (si la usas)

    expenses: {
      event: [], // event_expenses
      fixed: [], // fixed_expenses
    },

    activity: [], // event_activity
    pricingRules: [], // pricing_rules
    availability: [], // availability (si aplica)
  },

  // UI state (solo cosas de interfaz)
  ui: {
    route: "dashboard",
    loading: {
      global: false,
      events: false,
      contacts: false,
      venues: false,
      finance: false,
    },
    modals: {
      event: { open: false, mode: "create", eventId: null }, // mode: "create"|"edit"|"view"
      contact: { open: false, mode: "create", contactId: null },
      venue: { open: false, mode: "create", venueId: null },
    },
    toasts: [], // { id, type, message, createdAt }
    filters: {
      // filtros genéricos (sin imponer)
      events: {},
      contacts: {},
      venues: {},
    },
    preferences: {
      // cosas persistibles (tema, compact mode, etc.)
      persistUi: true,
      experienceMode: "management", // "management" | "my_events"
    },
  },

  // Errores / debug
  errors: {
    last: null, // { message, at, context }
  },
};

// =========================
// 2) Utilidades internas
// =========================
function deepClone(obj) {
  return structuredClone ? structuredClone(obj) : JSON.parse(JSON.stringify(obj));
}

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function mergeShallow(base, patch) {
  return { ...base, ...patch };
}

function nowIso() {
  return new Date().toISOString();
}

// =========================
// 3) Event Bus (simple)
// =========================
const listeners = new Map(); // eventName -> Set(callback)

function emit(eventName, payload) {
  const set = listeners.get(eventName);
  if (!set || set.size === 0) return;
  for (const cb of set) {
    try {
      cb(payload);
    } catch (e) {
      console.error(`[store] Error en listener "${eventName}"`, e);
    }
  }
}

function on(eventName, callback) {
  if (!listeners.has(eventName)) listeners.set(eventName, new Set());
  listeners.get(eventName).add(callback);

  return function off() {
    try {
      listeners.get(eventName)?.delete(callback);
    } catch (_) {
      // noop
    }
  };
}

// =========================
// 4) Store core
// =========================
let state = deepClone(initialState);
const subscribers = new Set(); // (state, prevState) => void

export function getState() {
  return state;
}

/**
 * Reemplaza el estado completo (raro; normalmente usar setState/patch).
 */
export function replaceState(nextState, { silent = false } = {}) {
  const prev = state;
  state = nextState;

  if (!silent) {
    for (const sub of subscribers) {
      try {
        sub(state, prev);
      } catch (e) {
        console.error("[store] Error en subscriber", e);
      }
    }
    emit("state:changed", { state, prev });
  }
}

/**
 * Aplica un patch shallow al root del estado.
 * Para cambios más profundos, usa setState(fn) con una copia.
 */
export function patchState(patch, { silent = false } = {}) {
  const prev = state;
  const next = mergeShallow(state, patch);
  replaceState(next, { silent });

  // Persistencia (si aplica)
  if (!silent) persistIfEnabled();
  emit("state:patched", { patch, state: next, prev });
}

/**
 * setState permite una función mutadora sobre una copia segura.
 * Ej:
 * setState(d => { d.ui.route = "events"; })
 */
export function setState(mutator, { silent = false } = {}) {
  const prev = state;
  const draft = deepClone(state);

  mutator(draft);

  replaceState(draft, { silent });
  if (!silent) persistIfEnabled();
  emit("state:set", { state: draft, prev });
}

/**
 * Suscripción a cambios del store.
 */
export function subscribe(callback) {
  subscribers.add(callback);
  return function unsubscribe() {
    subscribers.delete(callback);
  };
}

// =========================
// 5) Persistencia (mínima, opt-in)
// =========================
function buildPersistableSlice(fullState) {
  // Persistimos SOLO UI.preferences y algunos UI básicos si quieres.
  // No persistimos data de negocio para evitar inconsistencias con Supabase.
  return {
    meta: { version: STORE_VERSION },
    ui: {
      route: fullState.ui.route,
      preferences: fullState.ui.preferences,
      filters: fullState.ui.filters,
    },
  };
}

export function persistIfEnabled() {
  try {
    if (!state.ui?.preferences?.persistUi) return;
    const slice = buildPersistableSlice(state);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slice));
    emit("state:persisted", { at: nowIso() });
  } catch (e) {
    // No rompemos la app por localStorage
    console.warn("[store] No se pudo persistir el estado", e);
  }
}

export function hydrateFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;

    const saved = safeParse(raw);
    if (!saved?.meta?.version || saved.meta.version !== STORE_VERSION) {
      // versión distinta -> ignorar
      return false;
    }

    setState((d) => {
      // Solo mergeamos la parte persistible
      if (saved.ui?.route) d.ui.route = saved.ui.route;
      if (saved.ui?.preferences) d.ui.preferences = { ...d.ui.preferences, ...saved.ui.preferences };
      if (saved.ui?.filters) d.ui.filters = { ...d.ui.filters, ...saved.ui.filters };
    });

    emit("state:hydrated", { at: nowIso(), saved });
    return true;
  } catch (e) {
    console.warn("[store] No se pudo hidratar el estado", e);
    return false;
  }
}

export function clearStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    emit("state:storageCleared", { at: nowIso() });
  } catch (e) {
    console.warn("[store] No se pudo borrar storage", e);
  }
}

// =========================
// 6) Acciones UI (mínimas, sin negocio)
// =========================
export const actions = {
  // Routing
  setRoute(routeId) {
    setState((d) => {
      d.ui.route = routeId;
    });
    emit("ui:route", { route: routeId });
  },

  // Loading flags
  setLoading(key, value) {
    setState((d) => {
      if (!d.ui.loading) d.ui.loading = {};
      d.ui.loading[key] = Boolean(value);
    });
    emit("ui:loading", { key, value: Boolean(value) });
  },

  // Modals
  openModal(modalName, payload = {}) {
    setState((d) => {
      if (!d.ui.modals[modalName]) d.ui.modals[modalName] = { open: false };
      d.ui.modals[modalName] = { ...d.ui.modals[modalName], ...payload, open: true };
    });
    emit("ui:modal:open", { modal: modalName, payload });
  },

  closeModal(modalName) {
    setState((d) => {
      if (!d.ui.modals[modalName]) d.ui.modals[modalName] = { open: false };
      d.ui.modals[modalName].open = false;
    });
    emit("ui:modal:close", { modal: modalName });
  },

  // Toasts
  addToast({ type = "info", message = "", timeoutMs = 4000 } = {}) {
    const id = crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random());
    const toast = { id, type, message, createdAt: nowIso() };

    setState((d) => {
      d.ui.toasts.push(toast);
    });

    emit("ui:toast:add", toast);

    if (timeoutMs > 0) {
      setTimeout(() => {
        actions.removeToast(id);
      }, timeoutMs);
    }

    return id;
  },

  removeToast(id) {
    setState((d) => {
      d.ui.toasts = d.ui.toasts.filter((t) => t.id !== id);
    });
    emit("ui:toast:remove", { id });
  },

  // Error reporting
  setError(message, context = null) {
    setState((d) => {
      d.errors.last = { message, context, at: nowIso() };
    });
    emit("error:set", { message, context });
  },

  clearError() {
    setState((d) => {
      d.errors.last = null;
    });
    emit("error:clear", { at: nowIso() });
  },
};

// =========================
// 7) Exposición de bus (para pages/components)
// =========================
export const bus = { on, emit };

// =========================
// 8) Boot store (hidratación opcional)
// =========================
export function initStore() {
  // Intentamos hidratar preferencias si existe
  hydrateFromStorage();
  emit("store:ready", { at: nowIso() });
  return { getState, subscribe, setState, patchState, replaceState, actions, bus };
}
